/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  MutationKey,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { GramCore } from "../core.js";
import { usageSetBillingEmail } from "../funcs/usageSetBillingEmail.js";
import { combineSignals } from "../lib/primitives.js";
import { RequestOptions } from "../lib/sdks.js";
import { BillingEmail } from "../models/components/billingemail.js";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  SetBillingEmailRequest,
  SetBillingEmailSecurity,
} from "../models/operations/setbillingemail.js";
import { unwrapAsync } from "../types/fp.js";
import { useGramContext } from "./_context.js";
import { MutationHookOptions } from "./_types.js";

export type SetBillingEmailMutationVariables = {
  request: SetBillingEmailRequest;
  security?: SetBillingEmailSecurity | undefined;
  options?: RequestOptions;
};

export type SetBillingEmailMutationData = BillingEmail;

export type SetBillingEmailMutationError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * setBillingEmail usage
 *
 * @remarks
 * Set or clear the billing notification email for a PAYG organization
 */
export function useSetBillingEmailMutation(
  options?: MutationHookOptions<
    SetBillingEmailMutationData,
    SetBillingEmailMutationError,
    SetBillingEmailMutationVariables
  >,
): UseMutationResult<
  SetBillingEmailMutationData,
  SetBillingEmailMutationError,
  SetBillingEmailMutationVariables
> {
  const client = useGramContext();
  return useMutation({
    ...buildSetBillingEmailMutation(client, options),
    ...options,
  });
}

export function mutationKeySetBillingEmail(): MutationKey {
  return ["@gram/client", "usage", "setBillingEmail"];
}

export function buildSetBillingEmailMutation(
  client$: GramCore,
  hookOptions?: RequestOptions,
): {
  mutationKey: MutationKey;
  mutationFn: (
    variables: SetBillingEmailMutationVariables,
  ) => Promise<SetBillingEmailMutationData>;
} {
  return {
    mutationKey: mutationKeySetBillingEmail(),
    mutationFn: function setBillingEmailMutationFn({
      request,
      security,
      options,
    }): Promise<SetBillingEmailMutationData> {
      const mergedOptions = {
        ...hookOptions,
        ...options,
        fetchOptions: {
          ...hookOptions?.fetchOptions,
          ...options?.fetchOptions,
          signal: combineSignals(
            hookOptions?.fetchOptions?.signal,
            options?.fetchOptions?.signal,
          ),
        },
      };
      return unwrapAsync(usageSetBillingEmail(
        client$,
        request,
        security,
        mergedOptions,
      ));
    },
  };
}
